import React, { useState, useEffect } from 'react';
import { 
  getVouchers, 
  createVoucher, 
  updateVoucher, 
  deleteVoucher 
} from '../../api';
import { VOUCHER_TYPE, VOUCHER_STATUS } from '../../enums/voucher_type';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const VoucherManagement = () => {
  const [vouchers, setVouchers] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Form states
  const [formData, setFormData] = useState({
    voucher_type: '',
    title: '',
    description: '',
    discount_value: '',
    discount_type: 'AMOUNT',
    room_type_ids: [],
    code: '',
    start_date: '',
    end_date: '',
    booking_start_date: '',
    booking_end_date: '',
    redemption_count: ''
  });

  useEffect(() => {
    loadVouchers();
    loadRoomTypes();
  }, []);

  const loadRoomTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/room-types`);
      setRoomTypes(response.data);
    } catch (error) {
      console.error('Error loading room types:', error);
    }
  };

  // Auto-dismiss notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const loadVouchers = async () => {
    try {
      setLoading(true);
      const response = await getVouchers({
        search: searchTerm,
        type: typeFilter,
        status: statusFilter
      });
      
      // Check the voucher with code 7L7H9XSN
      const targetVoucher = response.vouchers.find(v => v.code === '7L7H9XSN');
      if (targetVoucher) {
        console.log('\n🔄 loadVouchers - RECEIVED VOUCHER 7L7H9XSN from API:');
        console.log('  booking_start_date:', targetVoucher.booking_start_date);
        console.log('  booking_end_date:', targetVoucher.booking_end_date);
        console.log('  start_date:', targetVoucher.start_date);
        console.log('  end_date:', targetVoucher.end_date);
      }
      
      setVouchers(response.vouchers || []);
      
      // Check after setting state
      if (targetVoucher) {
        console.log('💾 After setVouchers - checking state update\n');
      }
    } catch (error) {
      console.error('Error loading vouchers:', error);
      showNotification('Error loading vouchers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    loadVouchers();
  };

  const resetForm = () => {
    setFormData({
      voucher_type: '',
      title: '',
      description: '',
      discount_value: '',
      discount_type: 'AMOUNT',
      room_type_ids: [],
      code: '',
      start_date: '',
      end_date: '',
      booking_start_date: '',
      booking_end_date: '',
      redemption_count: ''
    });
    setSelectedVoucher(null);
    setIsEditing(false);
  };

  const handleCreateVoucher = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEditVoucher = (voucher) => {
    if (voucher.code === '7L7H9XSN') {
      console.log('\n🎯 ===== EDIT VOUCHER 7L7H9XSN =====');
      console.log('Full voucher object:', voucher);
      console.log('booking_start_date:', voucher.booking_start_date);
      console.log('booking_start_date type:', typeof voucher.booking_start_date);
      console.log('booking_end_date:', voucher.booking_end_date);
      console.log('booking_end_date type:', typeof voucher.booking_end_date);
    }
    
    setFormData({
      voucher_type: voucher.voucher_type,
      title: voucher.title,
      description: voucher.description || '',
      discount_value: voucher.discount_value || voucher.amount || '',
      discount_type: voucher.discount_type || 'AMOUNT',
      room_type_ids: voucher.room_type_ids || [],
      code: voucher.code,
      start_date: voucher.start_date ? voucher.start_date.split('T')[0] : '',
      end_date: voucher.end_date ? voucher.end_date.split('T')[0] : '',
      booking_start_date: voucher.booking_start_date ? voucher.booking_start_date.split('T')[0] : '',
      booking_end_date: voucher.booking_end_date ? voucher.booking_end_date.split('T')[0] : '',
      redemption_count: voucher.redemption_count || ''
    });
    
    if (voucher.code === '7L7H9XSN') {
      console.log('📝 Form data being set:');
      console.log('  booking_start_date raw:', voucher.booking_start_date);
      console.log('  booking_start_date after split:', voucher.booking_start_date ? voucher.booking_start_date.split('T')[0] : '');
      console.log('  booking_end_date raw:', voucher.booking_end_date);
      console.log('  booking_end_date after split:', voucher.booking_end_date ? voucher.booking_end_date.split('T')[0] : '');
      console.log('=====\n');
    }
    
    setSelectedVoucher(voucher);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare data with proper type conversions
      const submissionData = {
        ...formData,
        // Convert discount_value to number if it's a discount or booking voucher
        discount_value: formData.discount_value ? parseFloat(formData.discount_value) : null,
        // Convert redemption_count to number or 0 if empty for unlimited
        redemption_count: formData.redemption_count === '' || formData.redemption_count === null ? 
          0 : parseInt(formData.redemption_count, 10) || 0,
        // Ensure room_type_ids is included
        room_type_ids: formData.room_type_ids || [],
        // Explicitly include booking dates (convert empty strings to null)
        booking_start_date: formData.booking_start_date || null,
        booking_end_date: formData.booking_end_date || null
      };

      if (isEditing) {
        await updateVoucher(selectedVoucher.id, submissionData);
        showNotification('Voucher updated successfully!');
      } else {
        await createVoucher(submissionData);
        showNotification('Voucher created successfully!');
      }
      setShowModal(false);
      resetForm();
      loadVouchers();
    } catch (error) {
      console.error('Error saving voucher:', error);
      const errorMessage = error.response?.data?.error || 'Error saving voucher';
      showNotification(errorMessage, 'error');
    }
  };

  const handleDeleteVoucher = (voucher) => {
    setSelectedVoucher(voucher);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteVoucher(selectedVoucher.id);
      showNotification('Voucher deleted successfully!');
      setShowDeleteDialog(false);
      setSelectedVoucher(null);
      loadVouchers();
    } catch (error) {
      console.error('Error deleting voucher:', error);
      const errorMessage = error.response?.data?.error || 'Error deleting voucher';
      showNotification(errorMessage, 'error');
    }
  };

  const getStatusBadge = (voucher) => {
    const statusColors = {
      ACTIVE: '#28a745',
      EXPIRED: '#dc3545',
      FULLY_REDEEMED: '#6c757d',
      PARTIALLY_REDEEMED: '#ffc107',
      INACTIVE: '#dc3545'
    };
    
    return (
      <span style={{
        backgroundColor: statusColors[voucher.status] || '#6c757d',
        color: '#fff',
        padding: '4px 8px',
        borderRadius: 4,
        fontSize: '0.8em',
        fontWeight: 500
      }}>
        {voucher.status?.replace(/_/g, ' ')}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Backend now returns dates as strings (YYYY-MM-DD) thanks to dateStrings: true
    // Parse without timezone conversion by using local date constructor
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(year, month - 1, day);
    const result = date.toLocaleDateString();
    return result;
  };

  if (loading) {
    return <div style={{ padding: 20, textAlign: 'center' }}>Loading vouchers...</div>;
  }

  return (
    <div style={{ padding: 20 }}>
      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: 20,
          right: 20,
          padding: '12px 24px',
          borderRadius: 6,
          color: '#fff',
          backgroundColor: notification.type === 'success' ? '#28a745' : '#dc3545',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          {notification.message}
        </div>
      )}

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 20 
      }}>
        <h2 style={{ color: '#2a3b5c', margin: 0 }}>Voucher Management</h2>
        <button
          onClick={handleCreateVoucher}
          style={{
            backgroundColor: '#4f8cff',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '10px 20px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Create Voucher
        </button>
      </div>

      {/* Search and Filters */}
      <div style={{
        display: 'flex',
        gap: 10,
        marginBottom: 20,
        padding: 20,
        backgroundColor: '#f8f9fa',
        borderRadius: 8
      }}>
        <input
          type="text"
          placeholder="Search vouchers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: 4
          }}
        />
        
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: 4
          }}
        >
          <option value="">All Types</option>
          {Object.values(VOUCHER_TYPE).map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: 4
          }}
        >
          <option value="">All Status</option>
          {Object.values(VOUCHER_STATUS).map(status => (
            <option key={status} value={status}>{status.replace(/_/g, ' ')}</option>
          ))}
        </select>
        
        <button
          onClick={handleSearch}
          style={{
            backgroundColor: '#6c757d',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '8px 16px',
            cursor: 'pointer'
          }}
        >
          Search
        </button>
      </div>

      {/* Vouchers Table */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Code</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Title</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Type</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Discount</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Valid Period</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Usage</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Status</th>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {vouchers.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                  No vouchers found
                </td>
              </tr>
            ) : (
              vouchers.map((voucher) => (
                <tr key={voucher.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 12, fontWeight: 600, color: '#2a3b5c' }}>
                    {voucher.code}
                  </td>
                  <td style={{ padding: 12 }}>{voucher.title}</td>
                  <td style={{ padding: 12 }}>
                    <span style={{
                      backgroundColor: voucher.voucher_type === 'BOOKING' ? '#e3f2fd' : 
                                       voucher.voucher_type === 'DISCOUNT' ? '#f3e5f5' : '#e8f5e8',
                      color: voucher.voucher_type === 'BOOKING' ? '#1976d2' : 
                             voucher.voucher_type === 'DISCOUNT' ? '#7b1fa2' : '#388e3c',
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: '0.8em'
                    }}>
                      {voucher.voucher_type}
                    </span>
                  </td>
                  <td style={{ padding: 12 }}>
                    {voucher.discount_value ? 
                      (voucher.discount_type === 'PERCENTAGE' ? 
                        `${voucher.discount_value}%` : 
                        `₱${voucher.discount_value}`) : 
                      '-'}
                  </td>
                  <td style={{ padding: 12, fontSize: '0.9em' }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>Redemption:</div>
                      <div>{formatDate(voucher.start_date)} - {formatDate(voucher.end_date)}</div>
                      {voucher.booking_start_date && voucher.booking_end_date && (
                        <div style={{ marginTop: 4, fontSize: '0.85em', color: '#666' }}>
                          <div style={{ fontWeight: 500 }}>Check-in:</div>
                          <div>{formatDate(voucher.booking_start_date)} - {formatDate(voucher.booking_end_date)}</div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: 12 }}>
                    {voucher.redemption_used} / {voucher.redemption_count || '∞'}
                  </td>
                  <td style={{ padding: 12 }}>
                    {getStatusBadge(voucher)}
                  </td>
                  <td style={{ padding: 12 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handleEditVoucher(voucher)}
                        style={{
                          backgroundColor: '#ffc107',
                          color: '#212529',
                          border: 'none',
                          borderRadius: 4,
                          padding: '4px 8px',
                          fontSize: '0.8em',
                          cursor: 'pointer'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteVoucher(voucher)}
                        style={{
                          backgroundColor: '#dc3545',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          padding: '4px 8px',
                          fontSize: '0.8em',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: 8,
            padding: 24,
            minWidth: 500,
            maxWidth: 600,
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0, color: '#2a3b5c' }}>
              {isEditing ? 'Edit Voucher' : 'Create New Voucher'}
            </h3>
            
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Voucher Type *
                </label>
                <select
                  value={formData.voucher_type}
                  onChange={(e) => setFormData({...formData, voucher_type: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 4
                  }}
                >
                  <option value="">Select Type</option>
                  {Object.values(VOUCHER_TYPE).map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 4
                  }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    resize: 'vertical'
                  }}
                />
              </div>

              {formData.voucher_type === 'DISCOUNT' && (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                      Discount Type *
                    </label>
                    <select
                      value={formData.discount_type}
                      onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                      required
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: 4
                      }}
                    >
                      <option value="AMOUNT">Fixed Amount (₱)</option>
                      <option value="PERCENTAGE">Percentage (%)</option>
                    </select>
                  </div>
                  
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                      {formData.discount_type === 'PERCENTAGE' ? 'Discount Percentage (%) *' : 'Discount Amount (₱) *'}
                    </label>
                    <input
                      type="number"
                      step={formData.discount_type === 'PERCENTAGE' ? '1' : '0.01'}
                      min="0"
                      max={formData.discount_type === 'PERCENTAGE' ? '100' : undefined}
                      value={formData.discount_value}
                      onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                      required={formData.voucher_type === 'DISCOUNT' || formData.voucher_type === 'BOOKING'}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: 4
                      }}
                    />
                    {formData.discount_type === 'PERCENTAGE' && (
                      <small style={{ color: '#666', fontSize: '0.8em' }}>
                        Enter value between 0 and 100
                      </small>
                    )}
                  </div>
                </>
              )}

              {(formData.voucher_type === 'DISCOUNT' || formData.voucher_type === 'BOOKING') && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                    Applicable Room Types
                  </label>
                  <div style={{ 
                    maxHeight: '150px', 
                    overflowY: 'auto', 
                    border: '1px solid #ddd', 
                    padding: '8px',
                    borderRadius: 4,
                    backgroundColor: '#f9f9f9'
                  }}>
                    {roomTypes.map(roomType => (
                      <label key={roomType.id} style={{ display: 'block', marginBottom: '6px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.room_type_ids.includes(roomType.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ 
                                ...formData, 
                                room_type_ids: [...formData.room_type_ids, roomType.id] 
                              });
                            } else {
                              setFormData({ 
                                ...formData, 
                                room_type_ids: formData.room_type_ids.filter(id => id !== roomType.id) 
                              });
                            }
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        {roomType.name}
                      </label>
                    ))}
                  </div>
                  <small style={{ color: '#666', fontSize: '0.8em' }}>
                    Select specific room types or leave unchecked to apply to all room types
                  </small>
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Voucher Code
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  maxLength="12"
                  placeholder="Leave empty to auto-generate"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 4
                  }}
                />
                <small style={{ color: '#666', fontSize: '0.8em' }}>
                  Max 12 characters, alphanumeric only. Leave empty to auto-generate.
                </small>
              </div>

              <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 4
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 4
                    }}
                  />
                </div>
              </div>

              {/* Optional Booking Date Range */}
              <div style={{ 
                marginBottom: 16, 
                padding: 12, 
                background: '#f8f9fa', 
                borderRadius: 4,
                border: '1px solid #dee2e6'
              }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: 8, 
                  fontWeight: 600,
                  color: '#495057' 
                }}>
                  Booking Date Restrictions (Optional)
                </label>
                <p style={{ 
                  fontSize: '0.85em', 
                  color: '#6c757d', 
                  marginBottom: 12,
                  lineHeight: '1.4'
                }}>
                  Optionally restrict this voucher to only be valid for reservations with check-in dates within a specific range. Leave both fields empty to allow any check-in date.
                </p>
                
                <div style={{ display: 'flex', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: '0.9em' }}>
                      Earliest Check-in Date
                    </label>
                    <input
                      type="date"
                      value={formData.booking_start_date}
                      onChange={(e) => setFormData({...formData, booking_start_date: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: 4
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: '0.9em' }}>
                      Latest Check-in Date
                    </label>
                    <input
                      type="date"
                      value={formData.booking_end_date}
                      onChange={(e) => setFormData({...formData, booking_end_date: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: 4
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Redemption Limit
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.redemption_count}
                  onChange={(e) => setFormData({...formData, redemption_count: e.target.value})}
                  placeholder="Leave empty for unlimited"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 4
                  }}
                />
                <small style={{ color: '#666', fontSize: '0.8em' }}>
                  Maximum number of times this voucher can be used. Leave empty for unlimited use.
                </small>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    backgroundColor: '#6c757d',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    padding: '10px 20px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    backgroundColor: '#4f8cff',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    padding: '10px 20px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {isEditing ? 'Update' : 'Create'} Voucher
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && selectedVoucher && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: 8,
            padding: 24,
            minWidth: 400,
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#dc3545', marginTop: 0 }}>Confirm Delete</h3>
            <p>Are you sure you want to delete the voucher:</p>
            <p style={{ fontWeight: 600, color: '#2a3b5c' }}>
              {selectedVoucher.code} - {selectedVoucher.title}
            </p>
            <p style={{ fontSize: '0.9em', color: '#666' }}>
              This action cannot be undone.
            </p>
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 20 }}>
              <button
                onClick={() => setShowDeleteDialog(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  padding: '10px 20px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  padding: '10px 20px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoucherManagement;
